import { useEffect } from 'react';

/**
 * Closes a modal/dialog when the user switches away from the browser tab.
 *
 * Problem: modals render a `position:fixed; inset:0` backdrop. If the user
 * opens a modal and then switches tabs, the backdrop stays in the DOM on
 * return and silently intercepts every click — the page looks frozen.
 *
 * Fix: call `onClose` when `document.visibilityState` becomes `'hidden'`,
 * so the backdrop is gone before the user comes back.
 *
 * Exception: opening a link in a new tab (`target="_blank"` — e.g. an Instagram
 * profile or a post permalink from inside the modal) also flips the tab to
 * `hidden`. That's an intentional click, not a tab switch, so we must NOT close
 * then — otherwise the card vanishes the moment the user opens any external link.
 * We record the last new-tab link click and skip the close if it just happened.
 *
 * Pass `enabled=false` while a destructive action is in progress so the
 * dialog isn't dismissed mid-flight (e.g. a hard-delete RPC).
 */
export function useCloseOnTabSwitch(onClose: () => void, enabled = true) {
  useEffect(() => {
    if (!enabled) return;
    let lastBlankLinkClick = 0;
    const onClick = (e: MouseEvent) => {
      const a = (e.target as HTMLElement | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (a && a.target === '_blank') lastBlankLinkClick = Date.now();
    };
    const handler = () => {
      if (document.visibilityState !== 'hidden') return;
      if (Date.now() - lastBlankLinkClick < 1000) return; // opened a link in a new tab — keep the modal
      onClose();
    };
    document.addEventListener('click', onClick, true);
    document.addEventListener('visibilitychange', handler);
    return () => {
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('visibilitychange', handler);
    };
  }, [onClose, enabled]);
}
